import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { PencilSquare } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { usePerformanceRecentTrades, RECENT_TRADES_PAGE_SIZE } from '../../hooks/usePerformanceData';
import { TradingContext } from '../../contexts/TradingContext';
import TradeJournalForm from "./TradeJournalForm";
import { resolveJournalTradeOpenAt, resolveJournalTradeSymbol } from './journalListUtils';
import { resolveClosedPositionPnl } from '../../utils/closedPositionPnl';

const TradeJournalModal = ({ showModal, handleCloseModal, journalToEdit, positionData, isAutoOpened, tradeId }) => {
  const { colors } = useTheme();
  const [templateEditMode, setTemplateEditMode] = useState(false);
  const [templateReady, setTemplateReady] = useState(false);
  const { selectedAccount, isPaperTrading } = useContext(TradingContext);
  const editingTemplate = templateEditMode && !journalToEdit;

  useEffect(() => {
    if (!showModal) {
      setTemplateEditMode(false);
      setTemplateReady(false);
    }
  }, [showModal]);
  const accountId = selectedAccount?.AccountID != null ? String(selectedAccount.AccountID) : undefined;
  const paperTrading = selectedAccount?.isPaperTrading ?? isPaperTrading;

  const { data: recentTradesData } = usePerformanceRecentTrades({
    accountId,
    paperTrading,
    limit: Math.max(RECENT_TRADES_PAGE_SIZE, 50),
    includeOpenPositions: true,
    enabled: !!tradeId && !!accountId && showModal,
  });

  const connectedTrade = useMemo(() => {
    if (!tradeId) return null;
    const positions = recentTradesData?.pages?.flatMap((page) => page?.positions ?? []) ?? [];
    if (positions.length === 0) return null;
    return positions.find((p) => p?.tradeId === tradeId) || null;
  }, [recentTradesData?.pages, tradeId]);

  const tradeStatus = useMemo(() => {
    const qty = Number(positionData?.Quantity);
    const closedFromPositionEvent =
      isAutoOpened && (
        positionData?.Deleted === true ||
        (Number.isFinite(qty) && Math.abs(qty) === 0)
      );
    if (closedFromPositionEvent) return 'closed';

    const raw = connectedTrade?.status;
    if (raw === 'open' || raw === 'closed') return raw;

    // Fallback: infer status from positionData if present
    const hasExit =
      positionData?.ClosedDateTime ||
      positionData?.ExitPrice != null ||
      positionData?.RealizedPnL != null;
    return hasExit ? 'closed' : 'open';
  }, [connectedTrade?.status, positionData, isAutoOpened]);

  const tradePnl = useMemo(() => {
    const fromPosition = resolveClosedPositionPnl(positionData, positionData);
    const connected = connectedTrade?.pnl;
    const connectedNumber = connected != null && !Number.isNaN(Number(connected))
      ? Number(connected)
      : null;
    // A just-closed trade often lands in recent trades with pnl 0 until fills
    // are finalized. Keep the P&L from the position we just had.
    if (connectedNumber != null && connectedNumber !== 0) return connectedNumber;
    if (fromPosition != null) return fromPosition;
    return connectedNumber;
  }, [connectedTrade?.pnl, positionData]);

  const tradeOpenAt = useMemo(
    () => resolveJournalTradeOpenAt(positionData, connectedTrade),
    [connectedTrade, positionData]
  );

  const tradeSymbol = useMemo(
    () => resolveJournalTradeSymbol(positionData, connectedTrade),
    [connectedTrade, positionData]
  );

  const formatCurrency = (v) => {
    if (v == null || Number.isNaN(Number(v))) return '—';
    const n = Number(v);
    const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return n >= 0 ? `+$${abs}` : `-$${abs}`;
  };

  const statusPillStyle = useMemo(() => {
    const isOpen = tradeStatus === 'open';
    return {
      padding: '2px 10px',
      borderRadius: 999,
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      backgroundColor: isOpen ? `${colors.primary}22` : `${colors.textMuted}20`,
      color: isOpen ? colors.primary : colors.textMuted,
      border: `1px solid ${isOpen ? `${colors.primary}55` : colors.border}`,
      lineHeight: 1.4,
    };
  }, [colors, tradeStatus]);

  return (
    <Modal 
      show={showModal} 
      onHide={handleCloseModal} 
      centered
      className={`trade-journal-modal${editingTemplate ? ' is-editing-template' : ''}`}
      dialogClassName="trade-journal-modal-dialog"
      contentClassName="trade-journal-modal-content"
      backdropClassName="modal-backdrop-blur"
      style={{ zIndex: 10050 }}
      container={typeof document !== 'undefined' ? document.body : undefined}
    >
      <Modal.Header 
        closeButton 
        className="trade-journal-modal-header"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Modal.Title
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                color: colors.text,
                fontSize: '1.15rem',
                fontWeight: 650,
                lineHeight: 1.2,
                margin: 0,
              }}
            >
              <PencilSquare size={20} style={{ color: colors.primary }} />
              {journalToEdit ? 'Journal' : editingTemplate ? 'Edit Template' : 'New Journal'}
              {tradeSymbol ? ` - ${tradeSymbol}` : ''}
            </Modal.Title>

            {tradeId && journalToEdit && (
              <div style={{ marginTop: 8, display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: '0.65rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    P&amp;L
                  </div>
                  <div
                    style={{
                      fontSize: '0.95rem',
                      fontWeight: 750,
                      color: tradePnl == null ? colors.textMuted : tradePnl >= 0 ? colors.success : colors.danger,
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1.2,
                    }}
                  >
                    {formatCurrency(tradePnl)}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, marginRight: 14 }}>
            {!journalToEdit && (
              <button
                type="button"
                aria-pressed={editingTemplate}
                aria-label={editingTemplate ? 'Done editing template' : 'Edit template'}
                disabled={!templateReady}
                onClick={() => setTemplateEditMode((current) => !current)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 36,
                  padding: '0 12px',
                  borderRadius: 18,
                  boxSizing: 'border-box',
                  backgroundColor: editingTemplate ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                  border: '1px solid #fff',
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  lineHeight: 1,
                  cursor: templateReady ? 'pointer' : 'default',
                  flexShrink: 0,
                  outline: 'none',
                  opacity: templateReady ? 1 : 0.45,
                  whiteSpace: 'nowrap',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                }}
                onMouseEnter={(e) => {
                  if (!templateReady) return;
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = editingTemplate ? 'rgba(255, 255, 255, 0.12)' : 'transparent';
                }}
              >
                {editingTemplate ? 'Done' : 'Edit template'}
              </button>
            )}
            {tradeId && (
              <span style={statusPillStyle}>
                {tradeStatus === 'open' ? 'Open' : 'Closed'}
              </span>
            )}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="trade-journal-modal-body">
        {isAutoOpened && positionData && !journalToEdit && !editingTemplate && (
          <div style={{ 
            backgroundColor: `${colors.primary}14`,
            border: `1px solid ${colors.primary}55`,
            borderRadius: 6, 
            padding: '10px 12px',
            marginBottom: 16,
            fontSize: '0.85rem'
          }}>
            <div style={{ color: colors.primary, fontSize: '0.84rem', lineHeight: 1.4, fontWeight: 500 }}>
              Track your results and capture insights while they're fresh in your mind.
            </div>
          </div>
        )}
        {showModal && (
          <TradeJournalForm
            postSubmitCallback={handleCloseModal}
            initialJournal={journalToEdit}
            positionData={positionData}
            isAutoOpened={isAutoOpened}
            tradeId={tradeId}
            tradeSymbol={tradeSymbol}
            tradeOpenAt={tradeOpenAt}
            templateEditMode={editingTemplate}
            onTemplateReady={setTemplateReady}
            onEditTemplate={() => setTemplateEditMode(true)}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default TradeJournalModal;
